import { BASE_URL } from '@/config';
import { Route } from '@/constants';
import { api } from '@/features/auth';
import { logout } from '@/features/auth/store';
import { store } from '@/store';

import { UserSchema } from '../types';

import { CreateEmailPreferencesRequest, EmailPreferencesSchema, EmailPreferencesTransformer, InviteUserRequest, UpdateEmailPreferencesRequest, UpdateUserRequest } from './types';
import { emailPreferencesTransformer } from '../helpers';

export const userApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getCurrentUser: builder.query<UserSchema, void>({
      query: () => ({
        url: `users/me`,
        method: 'GET',
        params: {
          fields: [
            'id',
            'status',
            'first_name',
            'last_name',
            'phone_number',
            'email',
            'language',
            'avatar',
            'title',
            'description',
            'role.id',
            'role.name',
            'company.id',
            'company.name',
            'company.activity_interests',
            'company.technology_interests',
            'company.development_status_interests',
            'company.capacity_interests',
            'company.location_interests',
            'feature_flags.*',
            'opportunity_view.*',
            'show_modal_ttcc',
          ],
          deep: JSON.stringify({
            opportunity_view: {
              _limit: 500,
            },
          }),
        },
      }),
      transformResponse: ({ data }) => {
        if (data.role.name === 'Administrator' || data.role.name === 'Manager') {
          store.dispatch(logout());
        }
        return data;
      },
      providesTags: [{ type: 'Users', id: 'me' }],
    }),
    updateUser: builder.mutation<void, UpdateUserRequest>({
      query: (data: UpdateUserRequest) => ({
        url: `users/me`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: [{ type: 'Users', id: 'me' }],
    }),
    getCompanyUsers: builder.query<UserSchema[], void>({
      query: () => ({
        url: `users`,
        method: 'GET',
        params: {
          fields: ['id', 'status', 'first_name', 'last_name', 'email', 'language', 'avatar', 'title', 'description'],
          filter: `{"company": {"users": {"_eq": "$CURRENT_USER"}}}`,
        },
      }),
      transformResponse: ({ data }) => data,
      providesTags: [{ type: 'Users', id: 'company' }],
    }),
    inviteUser: builder.mutation<void, InviteUserRequest>({
      query: (data) => ({
        url: `accounts/invite`,
        method: 'POST',
        body: { ...data, url: `${BASE_URL}${Route.InvitationSignUp}` },
      }),
      invalidatesTags: [{ type: 'Users', id: 'company' }],
    }),

    // ==================== EMAIL PREFERENCES ====================

    getEmailPreferences: builder.query<EmailPreferencesTransformer | null, string>({
      query: (userId: string) => ({
        url: `items/email_preferences`,
        method: 'GET',
        params: {
          filter: JSON.stringify({ user: { _eq: userId } }),
          fields: [
            'id',
            'user',
            'recommendations_by_interests',
            'recommendations_by_activity',
            'newsletter_mkt',
          ],
        },
      }),
      transformResponse: ({ data }) => {
        if (!data || data.length === 0) return null;
        // Transformar de snake_case a camelCase
        return emailPreferencesTransformer(data[0]);
      },
      providesTags: (result, error, userId) => [{ type: 'EmailPreferences', id: userId }],
    }),

    createEmailPreferences: builder.mutation<EmailPreferencesSchema, CreateEmailPreferencesRequest>({
      query: (data) => ({
        url: `items/email_preferences`,
        method: 'POST',
        body: {
          user: data.user,
          recommendations_by_interests: data.recommendations_by_interests ?? true,
          recommendations_by_activity: data.recommendations_by_activity ?? true,
          reminders_by_eoi: data.reminders_by_eoi ?? true,
          newsletter_mkt: data.newsletter_mkt ?? true,
        },
      }),
      transformResponse: ({ data }) => data,
      invalidatesTags: (result, error, { user }) => [{ type: 'EmailPreferences', id: user }],
    }),

    updateEmailPreferences: builder.mutation<
      EmailPreferencesSchema,
      { id: string; userId: string; data: UpdateEmailPreferencesRequest }
    >({
      query: ({ id, data }) => ({
        url: `items/email_preferences/${id}`,
        method: 'PATCH',
        body: data,
      }),
      transformResponse: ({ data }) => data,
      invalidatesTags: (result, error, { userId }) => [{ type: 'EmailPreferences', id: userId }],
    }),
  }),
});

export const {
  useGetCurrentUserQuery,
  useUpdateUserMutation,
  useGetCompanyUsersQuery,
  useInviteUserMutation,
  useGetEmailPreferencesQuery,
  useCreateEmailPreferencesMutation,
  useUpdateEmailPreferencesMutation } =
  userApi;
